import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { getGameColor, getGameName } from "../constants/theme";
import { Linkify } from "../components/Linkify";
import { toast } from "sonner";
import {
  Trophy, Gamepad2, Calendar, MapPin, Users, Euro, Download,
  Smartphone, Mail, Lock, User, AlertCircle, Share2,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const absUrl = (u) => {
  if (!u) return null;
  return (u.startsWith("http") || u.startsWith("data:")) ? u : `${API_URL}${u}`;
};

const formatDate = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
};

function isMobileDevice() {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export default function ShareableEventPage() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, login, register } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ email: "", password: "", name: "", pseudo: "" });

  const isTournament = type === "tournament";
  const deepLink = `arcanapp://${type}/${id}`;

  useEffect(() => {
    const endpoint = isTournament ? `/public/tournaments/${id}` : `/public/availabilities/${id}`;
    axios.get(`${API_URL}/api${endpoint}`)
      .then(r => setEvent(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id, isTournament]);

  // Try deep link on mobile
  useEffect(() => {
    if (isMobileDevice()) {
      window.location.href = deepLink;
    }
  }, [deepLink]);

  // If already authenticated, redirect to player event page
  useEffect(() => {
    if (isAuthenticated) {
      navigate(`/player/event/${type}/${id}`, { replace: true });
    }
  }, [isAuthenticated, navigate, type, id]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setError("");
    setAuthLoading(true);
    try {
      if (isLoginMode) {
        await login(form.email, form.password);
      } else {
        if (!form.name) { setError("Nom requis"); setAuthLoading(false); return; }
        await register({ email: form.email, password: form.password, name: form.name, pseudo: form.pseudo || form.name });
      }
      toast.success("Connecté !");
      navigate(`/player/event/${type}/${id}`);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur de connexion");
    } finally {
      setAuthLoading(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]" /></div>;
  if (!event) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-white">Événement non trouvé</div>;

  const gc = getGameColor(event.game);
  const pct = isTournament && event.capacity > 0 ? Math.round((event.participants_count / event.capacity) * 100) : 0;
  const org = event.organizer || event.creator;
  const orgName = event.source === "scraper" ? event.source_name : (org?.company_name || org?.pseudo || org?.name || "Organisateur");
  const orgLogo = absUrl(org?.logo_url || org?.avatar_url);
  const bannerAbs = absUrl(event.banner_url);

  // Partage réseaux sociaux (aperçu riche via endpoint OpenGraph)
  const shareOgUrl = `${API_URL}/api/share/${type}/${id}`;
  const shareTextStr = `${event.name || getGameName(event.game)}${event.format ? ` (${event.format})` : ''}${event.date ? ` le ${formatDate(event.date)}` : ''}`.trim();
  const shareLinks = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareTextStr + '\n' + shareOgUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareOgUrl)}`,
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-4 md:p-8" data-testid="shareable-event-page">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/events" className="flex items-center gap-2">
            <Trophy className="w-6 h-6 text-[#FF6B35]" />
            <span className="text-lg font-bold text-white"><span className="text-[#FF6B35]">Arcan</span>app</span>
          </Link>
          {/* Menu partage réseaux sociaux */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1877F2]/10 hover:bg-[#1877F2]/20 text-[#1877F2] text-sm" data-testid="share-social-btn" title="Partager">
                <Share2 className="w-4 h-4" /> <span>Partager</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#1a1a1a] border-[#333] w-48">
              <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#252525]">
                <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2" data-testid="share-facebook">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-[#1877F2]"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  <span className="text-white text-sm">Facebook</span>
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#252525]">
                <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2" data-testid="share-whatsapp">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-[#25D366]"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                  <span className="text-white text-sm">WhatsApp</span>
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(shareOgUrl + '\n\n' + shareTextStr); toast.success('Lien copié pour Instagram !'); }} className="cursor-pointer focus:bg-[#252525] flex items-center gap-2" data-testid="share-instagram">
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-[#E4405F]"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.882 0 1.441 1.441 0 012.882 0z"/></svg>
                <span className="text-white text-sm">Instagram</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(shareTextStr + '\n' + shareOgUrl); toast.success('Lien copié pour Discord !'); }} className="cursor-pointer focus:bg-[#252525] flex items-center gap-2" data-testid="share-discord">
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-[#5865F2]"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/></svg>
                <span className="text-white text-sm">Discord</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* App download banner for mobile */}
        {isMobileDevice() && (
          <div className="bg-[#121212] border border-[#333] rounded-xl p-4 mb-4 flex items-center gap-3" data-testid="mobile-app-banner">
            <Smartphone className="w-8 h-8 text-[#FF6B35] shrink-0" />
            <div className="flex-1">
              <p className="text-white text-sm font-medium">Ouvrir dans l'app</p>
              <p className="text-[#666] text-xs">Pour la meilleure expérience</p>
            </div>
            <div className="flex gap-2">
              <a href="https://play.google.com/store" target="_blank" rel="noopener noreferrer"
                className="px-3 py-1.5 rounded-lg bg-[#FF6B35] text-white text-xs font-medium" data-testid="dl-android">Android</a>
              <a href="https://apps.apple.com" target="_blank" rel="noopener noreferrer"
                className="px-3 py-1.5 rounded-lg bg-[#252525] text-white text-xs font-medium border border-[#333]" data-testid="dl-ios">iOS</a>
            </div>
          </div>
        )}

        {/* Event card */}
        <Card className="bg-[#1a1a1a] border-[#333] overflow-hidden mb-4">
          {isTournament && bannerAbs ? (
            <img src={bannerAbs} alt={event.name || "Bannière"} className="w-full h-auto block bg-[#0f0f0f]" data-testid="event-banner" />
          ) : (
            <div className="h-1.5 w-full" style={{ backgroundColor: isTournament ? gc : "#4ECDC4" }} />
          )}
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge style={{ backgroundColor: `${gc}20`, color: gc }} className="text-sm font-semibold gap-1">
                {isTournament ? <Trophy className="w-3.5 h-3.5" /> : <Gamepad2 className="w-3.5 h-3.5" />}
                {getGameName(event.game)}
              </Badge>
              {(event.format || (event.formats && event.formats[0])) && <Badge className="bg-[#252525] text-[#B0B0B0]">{event.format || event.formats[0]}</Badge>}
              <Badge className={isTournament ? "bg-[#FF6B35]/10 text-[#FF6B35]" : "bg-[#4ECDC4]/10 text-[#4ECDC4]"}>
                {isTournament ? "Tournoi" : "Partie"}
              </Badge>
              {isTournament && event.is_team && (
                <Badge className="bg-purple-500/15 text-purple-400">
                  <Users className="w-3 h-3 mr-1" />Équipe de {event.team_size || 2}
                </Badge>
              )}
            </div>

            <h1 className="text-xl font-bold text-white">{event.name || event.description || `Partie ${getGameName(event.game)}`}</h1>
            {event.description && <p className="text-[#B0B0B0] text-sm whitespace-pre-line"><Linkify>{event.description}</Linkify></p>}

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 text-[#B0B0B0]"><Calendar className="w-4 h-4 text-[#FF6B35]" />{formatDate(event.date)}</div>
              {(event.venue?.city || event.location?.city) && (
                <div className="flex items-center gap-2 text-[#B0B0B0]"><MapPin className="w-4 h-4 text-[#4ECDC4]" />{event.venue?.city || event.location?.city}</div>
              )}
              <div className="flex items-center gap-2 text-[#B0B0B0]"><Users className="w-4 h-4 text-[#7B68EE]" />{event.participants_count || event.participants?.length || 0}/{event.capacity || event.max_players}</div>
              {isTournament && <div className="flex items-center gap-2 text-[#B0B0B0]"><Euro className="w-4 h-4 text-[#FFCB05]" />{!event.price && event.price !== 0 ? "—" : event.price === 0 ? "Gratuit" : `${event.price}€`}</div>}
            </div>

            {isTournament && (
              <div>
                <div className="flex justify-between text-xs text-[#666] mb-1"><span>Remplissage</span><span>{pct}%</span></div>
                <div className="h-2 rounded-full bg-[#252525] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: pct >= 100 ? "#EF4444" : gc }} />
                </div>
              </div>
            )}

            {(event.organizer || event.creator) && (
              <div className="flex items-center gap-3 pt-3 border-t border-[#333]" data-testid="organizer-section">
                {orgLogo ? (
                  <img src={orgLogo} alt={orgName} className="w-9 h-9 rounded-full object-cover border border-[#333] bg-[#252525]" data-testid="organizer-logo" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-[#252525] flex items-center justify-center text-[#FF6B35] font-bold text-xs">
                    {(orgName || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <p className="text-white text-sm font-medium" data-testid="organizer-name">{orgName}</p>
                  <p className="text-[#666] text-xs">{event.source === "scraper" ? "Boutique" : isTournament ? "Organisateur" : "Créateur"}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Auth section */}
        {!showAuth ? (
          <div className="space-y-3">
            <button onClick={() => setShowAuth(true)}
              className={`w-full py-3 rounded-xl font-medium transition-colors ${
                isTournament ? "bg-[#FF6B35] hover:bg-[#E55A25] text-white" : "bg-[#4ECDC4] hover:bg-[#3DB8B0] text-black"
              }`} data-testid="show-auth-btn">
              {isTournament && event.is_team
                ? `S'inscrire en équipe de ${event.team_size || 2}`
                : isTournament ? "S'inscrire au tournoi" : "Rejoindre la partie"}
            </button>
            <Link to="/events" className="block text-center text-[#666] text-sm hover:text-white" data-testid="browse-all-link">
              Voir tous les événements
            </Link>
          </div>
        ) : (
          <Card className="bg-[#1a1a1a] border-[#333]">
            <CardContent className="p-5">
              <div className="flex gap-2 mb-4">
                <button onClick={() => { setIsLoginMode(true); setError(""); }}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${isLoginMode ? "bg-[#FF6B35] text-white" : "bg-[#252525] text-[#B0B0B0]"}`} data-testid="auth-login-tab">
                  Connexion
                </button>
                <button onClick={() => { setIsLoginMode(false); setError(""); }}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${!isLoginMode ? "bg-[#FF6B35] text-white" : "bg-[#252525] text-[#B0B0B0]"}`} data-testid="auth-register-tab">
                  Inscription
                </button>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-red-500/10 text-red-400 text-sm mb-3" data-testid="auth-error">
                  <AlertCircle className="w-4 h-4 shrink-0" />{error}
                </div>
              )}

              <form onSubmit={handleAuth} className="space-y-3">
                {!isLoginMode && (
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="Nom" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      className="bg-[#0a0a0a] border-[#333] text-white" data-testid="auth-name" />
                    <Input placeholder="Pseudo" value={form.pseudo} onChange={e => setForm(f => ({ ...f, pseudo: e.target.value }))}
                      className="bg-[#0a0a0a] border-[#333] text-white" data-testid="auth-pseudo" />
                  </div>
                )}
                <Input type="email" placeholder="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required
                  className="bg-[#0a0a0a] border-[#333] text-white" data-testid="auth-email" />
                <Input type="password" placeholder="Mot de passe" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required
                  className="bg-[#0a0a0a] border-[#333] text-white" data-testid="auth-password" />
                <Button type="submit" disabled={authLoading} className="w-full bg-[#FF6B35] hover:bg-[#E55A25]" data-testid="auth-submit">
                  {authLoading ? "..." : isLoginMode ? "Se connecter" : "S'inscrire"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
